import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { MessageSquareCode, Upload, CheckCircle, AlertTriangle, Loader2 } from 'lucide-react';
import { useDatabase } from '../../context/DatabaseContext';
import { useDataTransfer } from '../../context/DataTransferContext';
import { udtCommentApi } from '../../services/api';
import StuiSelect from '../../components/common/StuiSelect';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function triggerDownload(content, filename) {
  const blob = new Blob([content], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function UdtCommentPage() {
  const { dbSlug: slug } = useDatabase();
  const { trackTransfer } = useDataTransfer();

  // EU options — loaded once on mount
  const [euOptions, setEuOptions] = useState([]);
  useEffect(() => {
    if (!slug) return;
    udtCommentApi.getEngUnits(slug)
      .then(res => setEuOptions(res.data || []))
      .catch(() => setEuOptions([]));
  }, [slug]);

  // Step 1: Upload / Analyze
  const [dragOver, setDragOver]     = useState(false);
  const [analyzing, setAnalyzing]   = useState(false);
  const [fileName, setFileName]     = useState('');
  const [analyzeResult, setAnalyzeResult] = useState(null);
  const [analyzeError, setAnalyzeError]   = useState('');
  const fileInputRef = useRef(null);

  // Step 2: Configuration
  // modeSelections: { tagName: modeName }  — one entry per tag instance
  // rangeValues:    { tagName: { low, high, eu } }  — one entry per tag instance
  // qtyValues:      { tagName: qty }  — one entry per tag instance
  const [modeSelections, setModeSelections] = useState({});
  const [rangeValues, setRangeValues]       = useState({});
  const [qtyValues, setQtyValues]           = useState({});

  // Step 3: Generate
  const [generating, setGenerating]         = useState(false);
  const [generateResult, setGenerateResult] = useState(null);
  const [generateError, setGenerateError]   = useState('');
  const [validationErrors, setValidationErrors] = useState([]);

  // ─── Upload / Analyze ───────────────────────────────────────────────────────

  const handleFile = useCallback(async (file) => {
    if (!file || !file.name.toLowerCase().endsWith('.csv')) {
      setAnalyzeError('Please upload a .csv file.');
      return;
    }

    setFileName(file.name);
    setAnalyzeResult(null);
    setAnalyzeError('');
    setGenerateResult(null);
    setGenerateError('');
    setValidationErrors([]);
    setModeSelections({});
    setRangeValues({});
    setQtyValues({});
    setAnalyzing(true);

    try {
      const csvContent = await file.text();
      const res = await udtCommentApi.analyze(slug, csvContent);
      const data = res.data;
      setAnalyzeResult(data);

      // Mode selections: one entry per tag instance, defaulted to first option
      const initModes = {};
      data.tags
        .filter(t => data.datatypesNeedingMode.includes(t.datatype))
        .forEach(t => {
          const opts = data.modeOptions.filter(o => o.datatype === t.datatype);
          if (opts.length > 0) initModes[t.name] = opts[0].modeName;
        });
      setModeSelections(initModes);

      // Range values: one entry per tag instance that has range members
      const initRanges = {};
      const seenRangeTags = new Set();
      data.rangePrompts.forEach(p => {
        if (!seenRangeTags.has(p.tagName)) {
          seenRangeTags.add(p.tagName);
          initRanges[p.tagName] = { low: '', high: '', eu: '' };
        }
      });
      setRangeValues(initRanges);

      // Qty values: one entry per tag instance that has qty members
      const initQty = {};
      const seenQtyTags = new Set();
      data.qtyPrompts.forEach(p => {
        if (!seenQtyTags.has(p.tagName)) {
          seenQtyTags.add(p.tagName);
          initQty[p.tagName] = '';
        }
      });
      setQtyValues(initQty);

    } catch (err) {
      setAnalyzeError(err.response?.data?.error || err.message || 'Analyze failed.');
    } finally {
      setAnalyzing(false);
    }
  }, [slug]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = '';
  };

  // ─── Validation ─────────────────────────────────────────────────────────────

  const validate = useCallback(() => {
    const errors = [];

    // Range: low, high, eu must all be filled
    Object.entries(rangeValues).forEach(([tagName, v]) => {
      if (v.low === '' || v.low === null) errors.push(`${tagName}: Low is required`);
      if (v.high === '' || v.high === null) errors.push(`${tagName}: High is required`);
      if (!v.eu) errors.push(`${tagName}: EU is required`);
    });

    // Qty: must be a positive integer
    Object.entries(qtyValues).forEach(([tagName, qty]) => {
      if (!qty || parseInt(qty) <= 0) errors.push(`${tagName}: Array quantity must be > 0`);
    });

    return errors;
  }, [rangeValues, qtyValues]);

  // ─── Generate ───────────────────────────────────────────────────────────────

  const handleGenerate = async () => {
    if (!analyzeResult) return;

    const errors = validate();
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }
    setValidationErrors([]);
    setGenerating(true);
    setGenerateError('');
    setGenerateResult(null);

    try {
      // One RangeValueDto per tag instance
      const rangeArr = Object.entries(rangeValues).map(([tagName, v]) => ({
        tagName,
        low: parseFloat(v.low) || 0,
        high: parseFloat(v.high) || 0,
        eu: v.eu || '',
      }));

      // One QtyValueDto per tag instance
      const qtyArr = Object.entries(qtyValues).map(([tagName, qty]) => ({
        tagName,
        qty: parseInt(qty) || 0,
      }));

      const res = await udtCommentApi.generate(slug, {
        tags: analyzeResult.tags,
        modeSelections,
        rangeValues: rangeArr,
        qtyValues: qtyArr,
      });

      setGenerateResult(res.data);
      await trackTransfer(
        { name: res.data.filename, format: 'csv', rows: analyzeResult.tags.length },
        async () => { triggerDownload(res.data.csvContent, res.data.filename); }
      );
    } catch (err) {
      setGenerateError(err.response?.data?.error || err.message || 'Generation failed.');
    } finally {
      setGenerating(false);
    }
  };

  // ─── Derived state ──────────────────────────────────────────────────────────

  const needsConfig = analyzeResult && (
    analyzeResult.datatypesNeedingMode.length > 0 ||
    Object.keys(rangeValues).length > 0 ||
    Object.keys(qtyValues).length > 0
  );

  // Tags needing mode selection, grouped by datatype
  const modeTagsByDatatype = useMemo(() => {
    if (!analyzeResult) return {};
    const groups = {};
    analyzeResult.tags
      .filter(t => analyzeResult.datatypesNeedingMode.includes(t.datatype))
      .forEach(t => {
        if (!groups[t.datatype]) groups[t.datatype] = [];
        groups[t.datatype].push(t);
      });
    return groups;
  }, [analyzeResult]);

  // Tag instances with range prompts (one row per instance)
  const rangeTagNames = useMemo(() => Object.keys(rangeValues), [rangeValues]);

  // Tag instances with qty prompts (one row per instance)
  const qtyTagNames = useMemo(() => Object.keys(qtyValues), [qtyValues]);

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="dev-module import-template-page">
      <div className="dev-module-header">
        <div className="dev-module-header-left">
          <h1>
            <MessageSquareCode size={20} className="stui-imgupload-title-icon" />
            UDT Comment Generation
          </h1>
          <span className="dev-module-meta">PLC CSV Export → RSLogix 5000 Import</span>
        </div>
      </div>

      <div className="dev-module-content">
        <div className="import-steps-layout" style={{ maxWidth: '1200px', alignItems: 'flex-start' }}>

          {/* ═══════════ TILE 1: UPLOAD ═══════════ */}
          <div className="stui-step-panel" style={{ maxWidth: '360px' }}>
            <div className="stui-step-header">
              <span className="stui-step-number">1</span>
              <h3>Upload PLC Export</h3>
            </div>
            <p className="stui-step-desc">
              Drop a RSLogix 5000 CSV export. TAG rows are matched against the Tag XRef to generate comment rows.
            </p>

            <div
              className={`import-dropzone ${dragOver ? 'drag-over' : ''} ${analyzing ? 'validating' : ''}`}
              onDrop={handleDrop}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onClick={() => !analyzing && fileInputRef.current?.click()}
            >
              <input ref={fileInputRef} type="file" accept=".csv" onChange={handleFileSelect} className="stui-hidden" />
              {analyzing
                ? <><Loader2 size={28} className="stui-spinning" /><span>Analyzing...</span></>
                : <><Upload size={28} /><span>{fileName || 'Drop .csv file here or click to browse'}</span></>}
            </div>

            {analyzeError && (
              <div className="import-val-result fail stui-mt-sm">
                <div className="import-val-summary"><AlertTriangle size={18} /><span>{analyzeError}</span></div>
              </div>
            )}

            {analyzeResult && !analyzeError && (
              <div className="import-val-result pass stui-mt-sm">
                <div className="import-val-summary">
                  <CheckCircle size={18} />
                  <span>{analyzeResult.tags.length} tag{analyzeResult.tags.length !== 1 ? 's' : ''} found</span>
                </div>
                <div className="import-val-section">
                  <div className="import-val-tabs">
                    {[...new Set(analyzeResult.tags.map(t => t.datatype))].map(dt => {
                      const count = analyzeResult.tags.filter(t => t.datatype === dt).length;
                      return (
                        <div key={dt} className="import-val-tab-row records">
                          <span className="import-val-tab-name">{dt}</span>
                          <span className="import-val-tab-count">{count}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
                {analyzeResult.unknownDatatypes.length > 0 && (
                  <div className="import-val-section">
                    <div className="import-val-section-label error">Unknown Datatypes (skipped)</div>
                    <div className="import-val-tabs">
                      {analyzeResult.unknownDatatypes.map(dt => (
                        <div key={dt} className="import-val-tab-row error">
                          <AlertTriangle size={14} />
                          <span className="import-val-tab-name">{dt}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ═══════════ TILE 2: CONFIGURE ═══════════ */}
          {analyzeResult && (
            <div className="stui-step-panel">
              <div className="stui-step-header">
                <span className="stui-step-number">2</span>
                <h3>Configure</h3>
              </div>

              {!needsConfig && (
                <p className="stui-step-desc stui-text-muted">
                  No configuration needed.
                </p>
              )}

              {/* ── Mode Selections — per tag instance, grouped by datatype ── */}
              {Object.entries(modeTagsByDatatype).map(([dt, tags]) => {
                const opts = analyzeResult.modeOptions.filter(o => o.datatype === dt);
                return (
                  <div key={dt} className="stui-mb-20">
                    <div className="import-val-section-label records stui-mb-sm">
                      Mode — {dt}
                    </div>
                    <div className="stui-data-grid-container">
                      <div className="stui-data-grid-scroll-area">
                        <table className="stui-data-grid stui-data-grid-auto">
                          <thead>
                            <tr>
                              <th>Tag</th>
                              <th>Description</th>
                              <th style={{ width: 160 }}>Mode</th>
                            </tr>
                          </thead>
                          <tbody>
                            {tags.map(tag => (
                              <tr key={tag.name}>
                                <td className="stui-mono stui-text-nowrap stui-td-xs">{tag.name}</td>
                                <td className="stui-td-dim stui-td-xs">{tag.description}</td>
                                <td className="stui-text-nowrap">
                                  <div className="stui-row-md">
                                    {opts.map(o => (
                                      <label key={o.modeName} className="stui-checkbox">
                                        <input
                                          type="radio"
                                          className="stui-radio-input"
                                          name={`mode-${tag.name}`}
                                          value={o.modeName}
                                          checked={modeSelections[tag.name] === o.modeName}
                                          onChange={() => setModeSelections(prev => ({ ...prev, [tag.name]: o.modeName }))}
                                        />
                                        {o.modeLabel}
                                      </label>
                                    ))}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* ── Engineering Ranges — one row per tag instance ── */}
              {rangeTagNames.length > 0 && (
                <div className="stui-mb-20">
                  <div className="import-val-section-label records stui-mb-sm">
                    Engineering Ranges
                  </div>
                  <div className="stui-data-grid-container">
                    <div className="stui-data-grid-scroll-area">
                      <table className="stui-data-grid stui-data-grid-auto">
                        <thead>
                          <tr>
                            <th>Tag</th>
                            <th>Datatype</th>
                            <th style={{ width: 120 }}>Low</th>
                            <th style={{ width: 120 }}>High</th>
                            <th style={{ width: 200 }}>EU</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rangeTagNames.map(tagName => {
                            const v = rangeValues[tagName] || { low: '', high: '', eu: '' };
                            const datatype = analyzeResult.tags.find(t => t.name === tagName)?.datatype || '';
                            const hasError = validationErrors.some(e => e.startsWith(`${tagName}:`));
                            return (
                              <tr key={tagName} style={{ background: hasError ? 'var(--color-error-light)' : undefined }}>
                                <td className="stui-mono stui-text-nowrap stui-td-xs">{tagName}</td>
                                <td className="stui-td-dim stui-td-xs">{datatype}</td>
                                <td>
                                  <input type="number" value={v.low} placeholder="0.0"
                                    className="stui-field-input stui-field-input-xs" style={{ borderColor: validationErrors.some(e => e === `${tagName}: Low is required`) ? 'var(--color-error)' : undefined }}
                                    onChange={e => { setRangeValues(prev => ({ ...prev, [tagName]: { ...prev[tagName], low: e.target.value } })); setValidationErrors([]); }} />
                                </td>
                                <td>
                                  <input type="number" value={v.high} placeholder="100.0"
                                    className="stui-field-input stui-field-input-xs" style={{ borderColor: validationErrors.some(e => e === `${tagName}: High is required`) ? 'var(--color-error)' : undefined }}
                                    onChange={e => { setRangeValues(prev => ({ ...prev, [tagName]: { ...prev[tagName], high: e.target.value } })); setValidationErrors([]); }} />
                                </td>
                                <td>
                                  <StuiSelect
                                    value={v.eu}
                                    className="stui-field-input stui-field-input-xs" style={{ borderColor: validationErrors.some(e => e === `${tagName}: EU is required`) ? 'var(--color-error)' : undefined }}
                                    onChange={e => { setRangeValues(prev => ({ ...prev, [tagName]: { ...prev[tagName], eu: e.target.value } })); setValidationErrors([]); }}
                                  >
                                    <option value="">— Select EU —</option>
                                    {euOptions.map(eu => (
                                      <option key={eu.eu} value={eu.eu}>{eu.eu}{eu.euDesc ? ` — ${eu.euDesc}` : ''}</option>
                                    ))}
                                  </StuiSelect>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Array Quantities — one row per tag instance ── */}
              {qtyTagNames.length > 0 && (
                <div className="stui-mb-20">
                  <div className="import-val-section-label records stui-mb-sm">
                    Array Quantities
                  </div>
                  <div className="stui-data-grid-container">
                    <div className="stui-data-grid-scroll-area">
                      <table className="stui-data-grid stui-data-grid-auto">
                        <thead>
                          <tr>
                            <th>Tag</th>
                            <th>Datatype</th>
                            <th style={{ width: 120 }}>Qty</th>
                          </tr>
                        </thead>
                        <tbody>
                          {qtyTagNames.map(tagName => {
                            const datatype = analyzeResult.tags.find(t => t.name === tagName)?.datatype || '';
                            return (
                              <tr key={tagName} style={{ background: validationErrors.some(e => e.startsWith(`${tagName}:`)) ? 'var(--color-error-light)' : undefined }}>
                                <td className="stui-mono stui-text-nowrap stui-td-xs">{tagName}</td>
                                <td className="stui-td-dim stui-td-xs">{datatype}</td>
                                <td>
                                  <input type="number" min="1" value={qtyValues[tagName] || ''} placeholder="0"
                                    className="stui-field-input stui-field-input-xs" style={{ width: 80, borderColor: validationErrors.some(e => e.startsWith(`${tagName}:`)) ? 'var(--color-error)' : undefined }}
                                    onChange={e => { setQtyValues(prev => ({ ...prev, [tagName]: e.target.value })); setValidationErrors([]); }} />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Validation errors ── */}
              {validationErrors.length > 0 && (
                <div className="import-val-result fail stui-mb-sm">
                  <div className="import-val-summary"><AlertTriangle size={18} /><span>Fix the following before generating:</span></div>
                  <div className="import-val-section">
                    {validationErrors.map((err, i) => (
                      <div key={i} className="stui-text-xs stui-text-error" style={{ padding: '2px 0' }}>{err}</div>
                    ))}
                  </div>
                </div>
              )}

              <button
                className="stui-btn stui-btn-primary"
                onClick={handleGenerate}
                disabled={generating || !analyzeResult}
              >
                {generating
                  ? <><Loader2 size={16} className="stui-spinning" /> Generating...</>
                  : <><MessageSquareCode size={16} /> Generate Comments</>}
              </button>

              {generateError && (
                <div className="import-val-result fail stui-mt-sm">
                  <div className="import-val-summary"><AlertTriangle size={18} /><span>{generateError}</span></div>
                </div>
              )}
            </div>
          )}

          {/* ═══════════ TILE 3: RESULT ═══════════ */}
          {generateResult && (
            <div className="stui-step-panel" style={{ maxWidth: '360px' }}>
              <div className="stui-step-header">
                <span className="stui-step-number">3</span>
                <h3>Result</h3>
              </div>

              <div className="import-val-result pass">
                <div className="import-val-summary">
                  <CheckCircle size={20} />
                  <span className="stui-break-all">{analyzeResult.tags.length} tag{analyzeResult.tags.length !== 1 ? 's' : ''} processed — downloading {generateResult.filename}</span>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>

    </div>
  );
}
